import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WorkflowViewState } from '@aline/common';
import { params } from '@aline/routes';
import { useApolloClient } from '@apollo/client/react';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import PQueue from 'p-queue';
import { useSearchParams } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { useDebouncedCallback } from 'use-debounce';

import {
  getBaselineBuilderState,
  getChartsWithTitle,
  getMutationFailureMessage,
  getStateWithCharts,
} from './insight-chart-builder-state';
import {
  getAppliedWorkflowListStateFromWorkflowView,
  WORKFLOW_LIST_CHART_ID,
} from '../workflow/workflow-list/workflow-list-charts';
import type { WorkflowListChartsIntegrationSlice } from '../workflow/workflow-list/workflow-list-charts';

type ViewSnapshot = {
  name: string;
  state: WorkflowViewState;
  viewId: string;
};

type EditorSession = {
  draftState: WorkflowViewState;
  initialState: WorkflowViewState;
  viewId: string;
};

type InsightBuilderView = NonNullable<Apollo.GetWorkflowViewQuery['workflowView']>;

/**
 * Owns the builder draft and the debounced persist queue. Discrete mutations
 * (add/delete chart, leave the page) call `flush` or `cancel` so they do not
 * share that queue.
 */
export function useInsightBuilderDraft(options: {
  canEdit: boolean;
  paused: boolean;
  view: InsightBuilderView | null | undefined;
  viewId: string | undefined;
}) {
  const { canEdit, paused, view, viewId } = options;
  const [name, setName] = useState('');
  const apolloClient = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastQueuedSnapshotRef = useRef<ViewSnapshot | null>(null);
  const [editorSession, setEditorSession] = useState<EditorSession | null>(null);
  const autosaveQueueRef = useRef(new PQueue({ concurrency: 1 }));

  const currentState = editorSession?.draftState ?? null;
  const chartsIntegrationSlice = useMemo<WorkflowListChartsIntegrationSlice>(
    () =>
      currentState
        ? getAppliedWorkflowListStateFromWorkflowView(currentState).chartsIntegration
        : {},
    [currentState]
  );
  const activeChartId =
    searchParams.get(params.insightChart) ??
    chartsIntegrationSlice.activeChartId ??
    WORKFLOW_LIST_CHART_ID;

  const persistViewSnapshot = useCallback(
    (snapshot: ViewSnapshot) => {
      return autosaveQueueRef.current.add(async () => {
        try {
          const result = await apolloClient.mutate<
            Apollo.UpdateWorkflowViewMutation,
            Apollo.UpdateWorkflowViewMutationVariables
          >({
            mutation: Apollo.UpdateWorkflowViewDocument,
            variables: {
              input: {
                id: snapshot.viewId,
                props: {
                  name: snapshot.name,
                  state: snapshot.state,
                },
              },
            },
          });
          if (result.data?.updateWorkflowView.workflowView) return;
          if (isEqual(lastQueuedSnapshotRef.current, snapshot))
            lastQueuedSnapshotRef.current = null;

          enqueueSnackbar(result.error?.message || 'Failed to autosave chart view.', {
            variant: 'error',
          });
        } catch (error) {
          if (isEqual(lastQueuedSnapshotRef.current, snapshot))
            lastQueuedSnapshotRef.current = null;

          enqueueSnackbar(getMutationFailureMessage(error, 'Failed to autosave chart view.'), {
            variant: 'error',
          });
        }
      });
    },
    [apolloClient, enqueueSnackbar]
  );
  const autosave = useDebouncedCallback((snapshot: ViewSnapshot) => {
    void persistViewSnapshot(snapshot);
  }, 500);

  useEffect(() => {
    if (
      !canEdit ||
      !currentState ||
      !view ||
      paused ||
      view.id !== viewId ||
      editorSession?.viewId !== view.id
    )
      return;

    const snapshot = { name, state: currentState, viewId: view.id };
    if (lastQueuedSnapshotRef.current && isEqual(snapshot, lastQueuedSnapshotRef.current)) return;

    lastQueuedSnapshotRef.current = snapshot;
    autosave(snapshot);
  }, [autosave, canEdit, currentState, editorSession?.viewId, name, paused, view, viewId]);

  const flush = useCallback(async () => {
    autosave.flush();
    await autosaveQueueRef.current.onIdle();
  }, [autosave]);

  const cancel = useCallback(async () => {
    autosave.cancel();
    await autosaveQueueRef.current.onIdle();
  }, [autosave]);

  const rememberPersistedState = useCallback(
    (state: WorkflowViewState) => {
      if (!viewId) return;
      lastQueuedSnapshotRef.current = { name, state, viewId };
      setEditorSession(previous =>
        previous?.viewId === viewId ? { ...previous, draftState: state } : previous
      );
    },
    [name, viewId]
  );

  const setActiveChartId = useCallback(
    (chartId: string) => {
      setSearchParams(previous => {
        previous.set(params.insightChart, chartId);
        return previous;
      });
    },
    [setSearchParams]
  );

  const handleViewStateChange = useCallback((nextState: WorkflowViewState) => {
    setEditorSession(previous => {
      if (!previous || isEqual(previous.draftState, nextState)) return previous;
      return { ...previous, draftState: nextState };
    });
  }, []);

  const handleChartSliceChange = useCallback(
    (slice: WorkflowListChartsIntegrationSlice) => {
      setEditorSession(previous => {
        if (!previous) return previous;
        const draftState = getStateWithCharts(previous.draftState, slice);
        if (isEqual(previous.draftState, draftState)) return previous;
        return { ...previous, draftState };
      });
      const nextActiveChartId = slice.activeChartId;
      if (!nextActiveChartId || nextActiveChartId === searchParams.get(params.insightChart)) return;
      setActiveChartId(nextActiveChartId);
    },
    [searchParams, setActiveChartId]
  );

  const handleChartTitleChange = useCallback(
    (chartId: string, title: string) => {
      const previousTitle = chartsIntegrationSlice.charts?.[chartId]?.title;
      if (previousTitle === title) return;
      handleChartSliceChange(getChartsWithTitle(chartsIntegrationSlice, chartId, title));
    },
    [chartsIntegrationSlice, handleChartSliceChange]
  );

  useEffect(
    () => () => {
      autosave.flush();
    },
    [autosave]
  );

  useEffect(() => {
    if (!editorSession || editorSession.viewId === viewId) return;
    autosave.cancel();
    setEditorSession(null);
  }, [autosave, editorSession, viewId]);

  useEffect(() => {
    if (!view || view.id !== viewId || editorSession?.viewId === view.id) return;

    /**
     * The grid reports its state through the same shape the editor saves, so the
     * baseline has to be the persisted View run through that same round trip.
     * Comparing against the raw persisted state, or against whatever the grid
     * happens to report first, reads as unsaved changes before any edit.
     */
    const baseline = getBaselineBuilderState(view.state, searchParams.get(params.insightChart));

    setName(view.name);
    setEditorSession({
      draftState: baseline.state,
      initialState: baseline.state,
      viewId: view.id,
    });
    lastQueuedSnapshotRef.current = {
      name: view.name,
      state: baseline.state,
      viewId: view.id,
    };
    setSearchParams(previous => {
      previous.set(params.insightChart, baseline.activeChartId);
      return previous;
    });
  }, [editorSession?.viewId, searchParams, setSearchParams, view, viewId]);

  return {
    activeChartId,
    cancel,
    chartsIntegrationSlice,
    currentState,
    editorSession,
    flush,
    handleChartSliceChange,
    handleChartTitleChange,
    handleViewStateChange,
    name,
    rememberPersistedState,
    setActiveChartId,
    setName,
  };
}
